import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, ImageIcon, Upload, Loader2 } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminBilingualInput from '@/components/admin/AdminBilingualInput';
import toast from 'react-hot-toast';
import { compressImage, formatBytes } from '@/lib/image-utils';
import type { Artist, BilingualText, SizeClass, Rarity } from '@/types';

const inputCls = "w-full px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all";
const labelCls = "block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2";

const SIZE_OPTIONS: SizeClass[] = ['small', 'medium', 'large'];
const RARITY_OPTIONS: Rarity[] = ['unique', 'limited', 'open', 'unknown'];

/* ── Image input row with upload + URL ── */
const ImageInputRow: React.FC<{
  url: string;
  onChange: (v: string) => void;
  onRemove?: () => void;
  canRemove: boolean;
}> = ({ url, onChange, onRemove, canRemove }) => {
  const ref = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);

  const handleFile = async (file: File): Promise<void> => {
    if (!file.type.startsWith('image/')) {
      toast.error(`"${file.name}" is not an image file`);
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error(`"${file.name}" exceeds 50MB limit (${formatBytes(file.size)})`);
      return;
    }
    setLoading(true);
    try {
      const { dataUrl, originalSize, compressedSize } = await compressImage(file);
      onChange(dataUrl);
      toast.success(`Compressed: ${formatBytes(originalSize)} → ${formatBytes(compressedSize)}`);
    } catch {
      toast.error(`Failed to process "${file.name}"`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-start gap-3">
      <div
        onClick={() => !loading && ref.current?.click()}
        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) void handleFile(f); }}
        onDragOver={(e) => e.preventDefault()}
        className="w-16 h-16 rounded-xl bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] flex-shrink-0 overflow-hidden flex items-center justify-center cursor-pointer hover:border-black/15 dark:hover:border-white/15 transition-all"
      >
        {loading ? (
          <Loader2 size={14} className="text-black/25 dark:text-white/25 animate-spin" />
        ) : url.trim() ? (
          <img src={url} alt="Preview" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        ) : (
          <Upload size={14} className="text-black/15 dark:text-white/15" />
        )}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = ''; }} />
      </div>
      <div className="flex-1">
        {url.startsWith('data:') ? (
          <div className="flex items-center gap-2">
            <span className={inputCls + ' flex-1 text-black/40 dark:text-white/40 cursor-default'}>
              (uploaded file)
            </span>
            <button
              type="button"
              onClick={() => onChange('')}
              className="px-2 py-2 text-[8px] font-bold uppercase tracking-wider text-red-500 hover:bg-red-500/5 rounded-lg transition-all shrink-0"
            >
              Clear
            </button>
          </div>
        ) : (
          <input
            type="text"
            value={url}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://example.com/image.jpg or click thumbnail to upload"
            className={inputCls}
          />
        )}
      </div>
      {canRemove && (
        <button type="button" onClick={onRemove} className="mt-2.5 w-8 h-8 rounded-lg flex items-center justify-center text-black/20 dark:text-white/20 hover:text-red-500 hover:bg-red-500/5 transition-all flex-shrink-0">
          <X size={14} />
        </button>
      )}
    </div>
  );
};

const AdminArtworkEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const artworks = useAdminStore((s) => s.artworks);
  const artists = useAdminStore((s) => s.artists);
  const categories = useAdminStore((s) => s.categories);
  const addArtwork = useAdminStore((s) => s.addArtwork);
  const updateArtwork = useAdminStore((s) => s.updateArtwork);
  const fieldLimits = useAdminStore((s) => s.settings?.fieldLimits);

  const isEdit = Boolean(id) && id !== 'new';
  const existing = isEdit ? artworks.find((a) => a._id === id) : undefined;

  const [title, setTitle] = useState<BilingualText>({ en: '', ka: '' });
  const [seriesName, setSeriesName] = useState<BilingualText>({ en: '', ka: '' });
  const [description, setDescription] = useState<BilingualText>({ en: '', ka: '' });
  const [category, setCategory] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  const [medium, setMedium] = useState<BilingualText>({ en: '', ka: '' });
  const [year, setYear] = useState<number | string>('');
  const [images, setImages] = useState<string[]>(['']);
  const [imageKeys, setImageKeys] = useState<number[]>([0]);
  const imageKeyCounter = useRef(1);
  const [priceAmount, setPriceAmount] = useState<number | string>(0);
  const [currency, setCurrency] = useState<'USD' | 'GEL'>('USD');
  const [sizeClass, setSizeClass] = useState<SizeClass>('medium');
  const [rarity, setRarity] = useState<Rarity>('limited');
  const [hasEdition, setHasEdition] = useState(true);
  const [editionCurrent, setEditionCurrent] = useState<number | string>(1);
  const [editionTotal, setEditionTotal] = useState<number | string>(1);
  const [width, setWidth] = useState<number | string>(0);
  const [height, setHeight] = useState<number | string>(0);
  const [dimUnit, setDimUnit] = useState<'cm' | 'in'>('cm');
  const [artistId, setArtistId] = useState('');
  const [isForSale, setIsForSale] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [sizeVariants, setSizeVariants] = useState<Array<{ _id: string; label: BilingualText; width: number | string; height: number | string; unit: 'cm' | 'in'; priceAmount: number | string }>>([]);
  // Per-artwork frame markups as percentages (0 = no markup, 15 = +15%)
  const [blackFrameMarkup, setBlackFrameMarkup] = useState<number | string>(0);
  const [whiteFrameMarkup, setWhiteFrameMarkup] = useState<number | string>(0);
  const [customFrameMarkup, setCustomFrameMarkup] = useState<number | string>(0);

  useEffect(() => {
    if (existing) {
      setTitle(existing.title);
      setSeriesName(existing.seriesName ?? { en: '', ka: '' });
      setDescription(existing.description);
      setCategory(existing.category);
      setCategoryId(existing.categoryId || '');
      setSubcategoryId(existing.subcategoryId || '');
      setMedium(existing.medium);
      setYear(existing.year && existing.year > 0 ? existing.year : '');
      // The store may still hold the public feed for this artwork: that response omits
      // images, and withholds pricing while the shop is closed. Every field is read
      // defensively so opening the editor before the admin fetch lands shows an incomplete
      // form rather than taking the page down.
      const imgs = existing.images && existing.images.length > 0 ? existing.images : [''];
      setImages(imgs);
      const keys = imgs.map(() => imageKeyCounter.current++);
      setImageKeys(keys);
      setPriceAmount((existing.price?.amount ?? 0) / 100);
      setCurrency(existing.price?.currency ?? 'USD');
      setSizeClass(existing.sizeClass ?? 'medium');
      setRarity(existing.rarity ?? 'unique');
      if (existing.edition) {
        setHasEdition(true);
        setEditionCurrent(existing.edition.current);
        setEditionTotal(existing.edition.total);
      } else {
        setHasEdition(false);
      }
      setWidth(existing.dimensions?.width ?? 0);
      setHeight(existing.dimensions?.height ?? 0);
      setDimUnit(existing.dimensions?.unit ?? 'cm');
      const existingArtistId = typeof existing.artist === 'string'
        ? existing.artist
        : ((existing.artist as Artist | undefined)?._id ?? '');
      setArtistId(existingArtistId);
      setIsForSale(existing.isForSale ?? true);
      setIsFeatured(existing.isFeatured ?? false);
      setVideoUrl(existing.videoUrl ?? '');
      if (existing.sizeVariants && existing.sizeVariants.length > 0) {
        setSizeVariants(existing.sizeVariants.map((v) => ({
          _id: v._id,
          label: v.label,
          width: v.dimensions.width,
          height: v.dimensions.height,
          unit: v.dimensions.unit,
          priceAmount: v.priceCents / 100,
        })));
      }
      // Load per-artwork frame markups
      const fm = existing.frameMarkups ?? {};
      setBlackFrameMarkup(fm.black ?? 0);
      setWhiteFrameMarkup(fm.white ?? 0);
      setCustomFrameMarkup(fm.custom ?? 0);
    }
  }, [existing]);

  const [saving, setSaving] = useState(false);
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (!title.en.trim()) { toast.error('Title (EN) is required'); return; }
      if (!artistId) { toast.error('Please select an artist'); return; }
      const selectedArtist = artists.find((a) => a._id === artistId);
      if (!selectedArtist) { toast.error('Selected artist not found'); return; }
      const filteredImages = images.filter((url) => url.trim() !== '');
      if (filteredImages.length === 0) { toast.error('At least one image is required'); return; }

      const selectedCategory = categories.find((c) => c._id === categoryId);
      const categoryString = selectedCategory ? selectedCategory.name.en : (category.trim() || 'Uncategorized');

      const artworkData = {
        title,
        seriesName: (seriesName.en.trim() || seriesName.ka.trim()) ? seriesName : undefined,
        description,
        category: categoryString,
        categoryId: categoryId || undefined,
        subcategoryId: subcategoryId || undefined,
        medium, year: year === '' || year === 0 ? 0 : Number(year),
        images: filteredImages,
        price: { amount: Math.round(Number(priceAmount || 0) * 100), currency },
        sizeClass, rarity,
        edition: hasEdition ? { current: Number(editionCurrent || 1), total: Number(editionTotal || 1) } : undefined,
        dimensions: { width: Number(width || 0), height: Number(height || 0), unit: dimUnit },
        artist: selectedArtist,
        isForSale, isFeatured,
        videoUrl: videoUrl.trim() || undefined,
        sizeVariants: sizeVariants.length > 0 ? sizeVariants.map((v) => ({
          _id: v._id,
          label: v.label,
          dimensions: { width: Number(v.width || 0), height: Number(v.height || 0), unit: v.unit },
          priceCents: Math.round(Number(v.priceAmount || 0) * 100),
        })) : undefined,
        frameMarkups: {
          black: Number(blackFrameMarkup || 0),
          white: Number(whiteFrameMarkup || 0),
          custom: Number(customFrameMarkup || 0),
        },
      };

      if (isEdit && id) {
        await updateArtwork(id, artworkData);
        toast.success('Artwork updated successfully!');
      } else {
        await addArtwork(artworkData);
        toast.success('Artwork created successfully!');
      }
      navigate('/admin/artworks');
    } catch (err) {
      console.error('Failed to save artwork:', err);
      toast.error('Failed to save artwork. Please try again.');
    } finally { setSaving(false); }
  };

  const addImageField = (): void => {
    setImages([...images, '']);
    setImageKeys([...imageKeys, imageKeyCounter.current++]);
  };
  const removeImageField = (index: number): void => {
    setImages(images.filter((_, i) => i !== index));
    setImageKeys(imageKeys.filter((_, i) => i !== index));
  };
  const updateImage = (index: number, value: string): void => { const u = [...images]; u[index] = value; setImages(u); };

  return (
    <form onSubmit={handleSubmit} className="space-y-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button type="button" onClick={() => navigate('/admin/artworks')} className="w-9 h-9 rounded-xl bg-black/[0.03] dark:bg-white/[0.05] flex items-center justify-center hover:bg-black/[0.06] dark:hover:bg-white/[0.08] transition-all">
            <ArrowLeft size={16} className="text-black/40 dark:text-white/40" />
          </button>
          <div>
            <h1 className="text-2xl font-syncopate font-bold tracking-tighter">
              {isEdit ? `Edit: ${existing?.title.en ?? 'Artwork'}` : 'New Artwork'}
            </h1>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">
              {isEdit ? 'Update artwork details' : 'Add a new artwork to the gallery'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate('/admin/artworks')} className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl border border-black/[0.08] dark:border-white/[0.08] text-black/50 dark:text-white/50 hover:border-black/20 dark:hover:border-white/20 transition-all">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity">
            {isEdit ? 'Save Changes' : 'Create Artwork'}
          </button>
        </div>
      </div>

      {/* Section 1: Title & Description */}
      <div className="space-y-4 p-6 bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl">
        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-4">Basic Information</h2>
        <AdminBilingualInput label="Title" value={title} onChange={setTitle} placeholder="Artwork title" maxLength={fieldLimits?.title} />
        <AdminBilingualInput label="Series Name (Optional)" value={seriesName} onChange={setSeriesName} placeholder="e.g. 'Tbilisi at Night' series" />
        <AdminBilingualInput label="Description" value={description} onChange={setDescription} textarea placeholder="Artwork description" maxLength={fieldLimits?.description} />
      </div>

      {/* Section 2: Category, Medium, Year */}
      <div className="space-y-4 p-6 bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl">
        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-4">Classification</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Category</label>
            <select
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value);
                setSubcategoryId('');
                const cat = categories.find((c) => c._id === e.target.value);
                if (cat) setCategory(cat.name.en);
              }}
              className={inputCls}
            >
              <option value="">-- Select category --</option>
              {categories
                .filter((c) => c.isActive)
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((c) => (
                  <option key={c._id} value={c._id}>{c.name.en}</option>
                ))}
            </select>
            {category && !categoryId && (
              <p className="mt-1 text-[9px] text-amber-600 dark:text-amber-400">
                Legacy category: &ldquo;{category}&rdquo; — Select a managed category above to migrate
              </p>
            )}
          </div>
          <div>
            <label className={labelCls}>Subcategory</label>
            <select
              value={subcategoryId}
              onChange={(e) => setSubcategoryId(e.target.value)}
              className={inputCls}
              disabled={!categoryId}
            >
              <option value="">-- {categoryId ? 'Select subcategory' : 'Select category first'} --</option>
              {categoryId && categories
                .find((c) => c._id === categoryId)
                ?.subcategories
                .filter((s) => s.isActive)
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((s) => (
                  <option key={s._id} value={s._id}>{s.name.en}</option>
                ))}
            </select>
          </div>
        </div>
        <AdminBilingualInput label="Medium" value={medium} onChange={setMedium} placeholder="e.g. Silver Gelatin Print" maxLength={fieldLimits?.shortDescription} />
        <div>
          <label className={labelCls}>Year (Optional)</label>
          <input type="number" value={year} onChange={(e) => setYear(e.target.value === '' ? '' : Number(e.target.value))} min={1800} max={2100} placeholder="Leave blank if unknown" className={inputCls} />
        </div>
      </div>

      {/* Section 3: Images with upload */}
      <div className="space-y-4 p-6 bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">Images</h2>
          <button type="button" onClick={addImageField} className="flex items-center gap-1.5 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.1em] rounded-lg bg-black/[0.04] dark:bg-white/[0.06] text-black/50 dark:text-white/50 hover:bg-black/[0.08] dark:hover:bg-white/[0.1] transition-all">
            <Plus size={10} /> Add Image
          </button>
        </div>
        <p className="text-[9px] text-black/30 dark:text-white/30 -mt-2 mb-3">
          Click thumbnail to upload a file, or paste a URL in the text field
        </p>
        <div className="space-y-3">
          {images.map((url, index) => (
            <ImageInputRow
              key={imageKeys[index] ?? index}
              url={url}
              onChange={(v) => updateImage(index, v)}
              onRemove={() => removeImageField(index)}
              canRemove={images.length > 1}
            />
          ))}
        </div>
      </div>

      {/* Section 4: Pricing */}
      <div className="space-y-4 p-6 bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl">
        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-4">Pricing</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Price (Dollars)</label>
            <input type="number" value={priceAmount} onChange={(e) => setPriceAmount(e.target.value === '' ? '' : Number(e.target.value))} min={0} step={0.01} placeholder="0.00" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Currency</label>
            <select value={currency} onChange={(e) => setCurrency(e.target.value as 'USD' | 'GEL')} className={inputCls}>
              <option value="USD">USD</option>
              <option value="GEL">GEL</option>
            </select>
          </div>
        </div>
      </div>

      {/* Section 5: Size Class & Rarity */}
      <div className="space-y-6 p-6 bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl">
        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-4">Attributes</h2>
        <div>
          <label className={labelCls}>Size Class</label>
          <div className="flex items-center gap-2">
            {SIZE_OPTIONS.map((size) => (
              <button key={size} type="button" onClick={() => setSizeClass(size)} className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] rounded-xl transition-all ${sizeClass === size ? 'bg-black dark:bg-white text-white dark:text-black' : 'bg-black/[0.03] dark:bg-white/[0.05] text-black/40 dark:text-white/40 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]'}`}>
                {size}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className={labelCls}>Rarity</label>
          <div className="flex items-center gap-2">
            {RARITY_OPTIONS.map((r) => (
              <button key={r} type="button" onClick={() => setRarity(r)} className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] rounded-xl transition-all ${rarity === r ? 'bg-black dark:bg-white text-white dark:text-black' : 'bg-black/[0.03] dark:bg-white/[0.05] text-black/40 dark:text-white/40 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]'}`}>
                {r}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Section 6: Edition */}
      <div className="space-y-4 p-6 bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl">
        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-4">Edition</h2>
        <div className="flex items-center gap-3">
          <label className={labelCls + ' mb-0'}>Limited Edition</label>
          <button type="button" onClick={() => setHasEdition(!hasEdition)} className={`relative w-11 h-6 rounded-full transition-all ${hasEdition ? 'bg-black dark:bg-white' : 'bg-black/[0.08] dark:bg-white/[0.1]'}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full transition-all ${hasEdition ? 'left-[22px] bg-white dark:bg-black' : 'left-0.5 bg-white dark:bg-black'}`} />
          </button>
        </div>
        {hasEdition && (
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <label className={labelCls}>Current Number</label>
              <input type="number" value={editionCurrent} onChange={(e) => setEditionCurrent(e.target.value === '' ? '' : Number(e.target.value))} min={1} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Total Editions</label>
              <input type="number" value={editionTotal} onChange={(e) => setEditionTotal(e.target.value === '' ? '' : Number(e.target.value))} min={1} className={inputCls} />
            </div>
          </div>
        )}
      </div>

      {/* Section 7: Dimensions */}
      <div className="space-y-4 p-6 bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl">
        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-4">Dimensions</h2>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Width</label>
            <input type="number" value={width} onChange={(e) => setWidth(e.target.value === '' ? '' : Number(e.target.value))} min={0} step={0.1} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Height</label>
            <input type="number" value={height} onChange={(e) => setHeight(e.target.value === '' ? '' : Number(e.target.value))} min={0} step={0.1} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Unit</label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setDimUnit('cm')} className={`flex-1 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.1em] rounded-xl transition-all ${dimUnit === 'cm' ? 'bg-black dark:bg-white text-white dark:text-black' : 'bg-black/[0.03] dark:bg-white/[0.05] text-black/40 dark:text-white/40 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]'}`}>
                cm
              </button>
              <button type="button" onClick={() => setDimUnit('in')} className={`flex-1 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.1em] rounded-xl transition-all ${dimUnit === 'in' ? 'bg-black dark:bg-white text-white dark:text-black' : 'bg-black/[0.03] dark:bg-white/[0.05] text-black/40 dark:text-white/40 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]'}`}>
                in
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Section 7.4: Per-Artwork Frame Markups (overrides global settings) */}
      <div className="space-y-4 p-6 bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl">
        <div>
          <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">Frame Markup % (Optional, Per Artwork)</h2>
          <p className="text-[10px] text-black/40 dark:text-white/40 mt-1">Frame price markup specific to this artwork. <strong>0</strong> = no markup, <strong>15</strong> = +15%. If 0, no surcharge will appear on the customer page.</p>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Black Frame Markup (%)</label>
            <input type="number" value={blackFrameMarkup} onChange={(e) => setBlackFrameMarkup(e.target.value === '' ? '' : Number(e.target.value))} step="0.1" min="0" max="500" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>White Frame Markup (%)</label>
            <input type="number" value={whiteFrameMarkup} onChange={(e) => setWhiteFrameMarkup(e.target.value === '' ? '' : Number(e.target.value))} step="0.1" min="0" max="500" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Custom Frame Markup (%)</label>
            <input type="number" value={customFrameMarkup} onChange={(e) => setCustomFrameMarkup(e.target.value === '' ? '' : Number(e.target.value))} step="0.1" min="0" max="500" className={inputCls} />
          </div>
        </div>
      </div>

      {/* Section 7.5: Size Variants (multiple sizes with different prices) */}
      <div className="space-y-4 p-6 bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">Size Variants (Optional)</h2>
            <p className="text-[10px] text-black/40 dark:text-white/40 mt-1">Add multiple sizes with different prices. If empty, uses main price above.</p>
          </div>
          <button type="button" onClick={() => setSizeVariants([...sizeVariants, { _id: `sv_${Date.now()}`, label: { en: '', ka: '' }, width: 0, height: 0, unit: 'cm', priceAmount: 0 }])} className="flex items-center gap-1.5 px-3 py-2 bg-black/[0.05] hover:bg-black/[0.1] text-[9px] font-bold uppercase tracking-wider rounded-lg transition-all">
            <Plus size={12} /> Add Size
          </button>
        </div>
        {sizeVariants.length > 0 && (
          <div className="space-y-3">
            {sizeVariants.map((sv, idx) => (
              <div key={sv._id} className="p-4 bg-black/[0.02] dark:bg-white/[0.03] rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-black/40 dark:text-white/40">Variant #{idx + 1}</span>
                  <button type="button" onClick={() => setSizeVariants(sizeVariants.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700 text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded">
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[9px] font-bold uppercase tracking-wider text-black/40 dark:text-white/40 block mb-1">Label EN</label>
                    <input type="text" value={sv.label.en} onChange={(e) => { const v = [...sizeVariants]; v[idx] = { ...sv, label: { ...sv.label, en: e.target.value } }; setSizeVariants(v); }} placeholder="e.g. Small" className={inputCls} />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold uppercase tracking-wider text-black/40 dark:text-white/40 block mb-1">Label KA</label>
                    <input type="text" value={sv.label.ka} onChange={(e) => { const v = [...sizeVariants]; v[idx] = { ...sv, label: { ...sv.label, ka: e.target.value } }; setSizeVariants(v); }} placeholder="მაგ: პატარა" className={inputCls} />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="text-[9px] font-bold uppercase tracking-wider text-black/40 dark:text-white/40 block mb-1">Width</label>
                    <input type="number" value={sv.width} onChange={(e) => { const v = [...sizeVariants]; v[idx] = { ...sv, width: e.target.value === '' ? '' : Number(e.target.value) }; setSizeVariants(v); }} min={0} step={0.1} className={inputCls} />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold uppercase tracking-wider text-black/40 dark:text-white/40 block mb-1">Height</label>
                    <input type="number" value={sv.height} onChange={(e) => { const v = [...sizeVariants]; v[idx] = { ...sv, height: e.target.value === '' ? '' : Number(e.target.value) }; setSizeVariants(v); }} min={0} step={0.1} className={inputCls} />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold uppercase tracking-wider text-black/40 dark:text-white/40 block mb-1">Unit</label>
                    <select value={sv.unit} onChange={(e) => { const v = [...sizeVariants]; v[idx] = { ...sv, unit: e.target.value as 'cm' | 'in' }; setSizeVariants(v); }} className={inputCls}>
                      <option value="cm">cm</option>
                      <option value="in">in</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[9px] font-bold uppercase tracking-wider text-black/40 dark:text-white/40 block mb-1">Price ({currency})</label>
                    <input type="number" value={sv.priceAmount} onChange={(e) => { const v = [...sizeVariants]; v[idx] = { ...sv, priceAmount: e.target.value === '' ? '' : Number(e.target.value) }; setSizeVariants(v); }} min={0} step={0.01} className={inputCls} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section 8: Artist */}
      <div className="space-y-4 p-6 bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl">
        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-4">Artist</h2>
        <div>
          <label className={labelCls}>Select Artist</label>
          <select value={artistId} onChange={(e) => setArtistId(e.target.value)} className={inputCls}>
            <option value="">-- Select an artist --</option>
            {artists.filter((a) => a.isActive).map((a) => (
              <option key={a._id} value={a._id}>{a.name.en}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Section 9: Visibility Toggles */}
      <div className="space-y-4 p-6 bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl">
        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-4">Visibility</h2>
        <div className="flex items-center justify-between">
          <div>
            <label className={labelCls + ' mb-0'}>For Sale</label>
            <p className="text-[10px] text-black/30 dark:text-white/30 mt-0.5">Display in shop with purchase option</p>
          </div>
          <button type="button" onClick={() => setIsForSale(!isForSale)} className={`relative w-11 h-6 rounded-full transition-all ${isForSale ? 'bg-black dark:bg-white' : 'bg-black/[0.08] dark:bg-white/[0.1]'}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full transition-all ${isForSale ? 'left-[22px] bg-white dark:bg-black' : 'left-0.5 bg-white dark:bg-black'}`} />
          </button>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-black/[0.04] dark:border-white/[0.04]">
          <div>
            <label className={labelCls + ' mb-0'}>Featured</label>
            <p className="text-[10px] text-black/30 dark:text-white/30 mt-0.5">Highlight on homepage and featured sections</p>
          </div>
          <button type="button" onClick={() => setIsFeatured(!isFeatured)} className={`relative w-11 h-6 rounded-full transition-all ${isFeatured ? 'bg-black dark:bg-white' : 'bg-black/[0.08] dark:bg-white/[0.1]'}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full transition-all ${isFeatured ? 'left-[22px] bg-white dark:bg-black' : 'left-0.5 bg-white dark:bg-black'}`} />
          </button>
        </div>
      </div>

      {/* Section 10: Video URL */}
      <div className="space-y-4 p-6 bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl">
        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-4">Media</h2>
        <div>
          <label className={labelCls}>Video URL (Optional)</label>
          <input type="text" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." className={inputCls} />
        </div>
      </div>

      {/* Bottom Save Bar */}
      <div className="flex items-center justify-end gap-2 pt-4 pb-8">
        <button type="button" onClick={() => navigate('/admin/artworks')} className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl border border-black/[0.08] dark:border-white/[0.08] text-black/50 dark:text-white/50 hover:border-black/20 dark:hover:border-white/20 transition-all">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity">
          {isEdit ? 'Save Changes' : 'Create Artwork'}
        </button>
      </div>
    </form>
  );
};

export default AdminArtworkEditorPage;
